import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { rules as rulesApi, sightings as sightingsApi, type AlertRule, type MinSize, type RecentAlert, type RuleDraft } from "../api/client";
import SetupChecklist from "../components/SetupChecklist";
import ZonePreview from "../components/ZonePreview";
import { altitudeMax, altitudeStep, altitudeUnit, feetToInput, inputToFeet, type Units } from "../units";
import { t } from "../i18n/en";
import { useSettings } from "../settings/useSettings";

const DEFAULT_DRAFT: Partial<RuleDraft> & { name: string } = {
  // PLAN D12 — low and close, the aircraft you would actually walk outside for.
  name: "Overhead",
  enabled: true,
  anchor_mode: "auto",
  radius_m: 2000,
  alt_min_ft: 0,
  alt_max_ft: 5000,
  lead_time_seconds: 120,
  cooldown_minutes: 60,
  respect_quiet_hours: true,
  daylight_only: true,
  min_size: "any",
};

function ago(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return t.alerts.justNow;
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

const clock = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

const km = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(m % 1000 === 0 ? 0 : 1)} km` : `${m} m`);
const mins = (s: number) => (s >= 60 ? `${Math.round(s / 60)} min` : `${s}s`);

function summary(rule: AlertRule, units: Units): string {
  return [
    km(rule.radius_m),
    // Same rounding as the editor inputs (100 m in metric), so a rule reads identically in both places.
    `${feetToInput(rule.alt_min_ft, units).toLocaleString("en-GB")}–${feetToInput(rule.alt_max_ft, units).toLocaleString("en-GB")} ${altitudeUnit(units)}`,
    `${mins(rule.lead_time_seconds)} ${t.alerts.lead}`,
    rule.min_size !== "any" ? t.alerts.sizeShort[rule.min_size] : null,
    rule.daylight_only ? t.alerts.daylightShort : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Live count of tracked aircraft passing the size and altitude filters — makes abstract settings
 *  concrete while editing. Debounced; uses the engine's own filter on the server. */
function usePreview(minSize: MinSize, altMin: number, altMax: number) {
  const [result, setResult] = useState<{ tracked: number; matching: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      rulesApi
        .preview({ min_size: minSize, alt_min_ft: altMin, alt_max_ft: altMax })
        .then((r) => !cancelled && setResult(r))
        .catch(() => !cancelled && setResult(null));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [minSize, altMin, altMax]);
  return result;
}

function Editor({
  rule,
  units,
  anchorLat,
  anchorLon,
  onSave,
  onDelete,
  onCancel,
}: {
  rule: AlertRule | null;
  units: Units;
  anchorLat: number | null;
  anchorLon: number | null;
  onSave: (draft: Partial<RuleDraft> & { name: string }) => Promise<void>;
  onDelete: (() => Promise<void>) | null;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Partial<RuleDraft> & { name: string }>(
    rule ? { ...rule } : { ...DEFAULT_DRAFT },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState("");

  async function sendTest() {
    setTesting(true);
    setTestResult("");
    try {
      const r = await rulesApi.test(draft);
      setTestResult(
        r.delivered === 0
          ? t.alerts.testNoDevice
          : r.sample
            ? t.alerts.testSentSample
            : t.alerts.testSent(r.aircraft ?? ""),
      );
    } catch (err) {
      setTestResult(err instanceof Error ? err.message : String(err));
    } finally {
      setTesting(false);
    }
  }

  const set = <K extends keyof RuleDraft>(key: K, value: RuleDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const preview = usePreview(draft.min_size ?? "any", draft.alt_min_ft ?? 0, draft.alt_max_ft ?? 5000);

  const centreLat = draft.anchor_mode === "fixed" ? (draft.center_lat ?? anchorLat) : anchorLat;
  const centreLon = draft.anchor_mode === "fixed" ? (draft.center_lon ?? anchorLon) : anchorLon;

  async function save() {
    setBusy(true);
    setError("");
    try {
      await onSave(draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <label htmlFor="rule-name">{t.alerts.name}</label>
      <input id="rule-name" value={draft.name} onChange={(e) => set("name", e.target.value)} maxLength={80} />

      <label htmlFor="rule-anchor">{t.alerts.anchor}</label>
      <select
        id="rule-anchor"
        value={draft.anchor_mode}
        onChange={(e) => set("anchor_mode", e.target.value as RuleDraft["anchor_mode"])}
      >
        <option value="auto">{t.alerts.anchorAuto}</option>
        <option value="home">{t.alerts.anchorHome}</option>
        <option value="live">{t.alerts.anchorLive}</option>
        <option value="fixed">{t.alerts.anchorFixed}</option>
      </select>
      <p className="hint">{t.alerts.anchorHint[draft.anchor_mode ?? "auto"]}</p>

      <ZonePreview
        lat={centreLat}
        lon={centreLon}
        radiusM={draft.radius_m ?? 2000}
        editable={draft.anchor_mode === "fixed"}
        onPick={(lat, lon) => {
          set("center_lat", lat);
          set("center_lon", lon);
        }}
      />
      {draft.anchor_mode === "fixed" && <p className="hint">{t.alerts.tapToPlace}</p>}

      <label htmlFor="rule-radius">
        {t.alerts.radius} — <strong>{km(draft.radius_m ?? 2000)}</strong>
      </label>
      <input
        id="rule-radius"
        type="range"
        min={200}
        max={20000}
        step={100}
        value={draft.radius_m ?? 2000}
        onChange={(e) => set("radius_m", Number(e.target.value))}
      />

      <div className="row">
        <div>
          <label htmlFor="rule-alt-min">{t.alerts.altMin(altitudeUnit(units))}</label>
          {/* Shown in the chosen unit, stored in feet: the engine and the feed both reason in feet. */}
          <input
            id="rule-alt-min"
            type="number"
            min={0}
            max={altitudeMax(units)}
            step={altitudeStep(units)}
            value={feetToInput(draft.alt_min_ft ?? 0, units)}
            onChange={(e) => set("alt_min_ft", inputToFeet(Number(e.target.value), units))}
          />
        </div>
        <div>
          <label htmlFor="rule-alt-max">{t.alerts.altMax(altitudeUnit(units))}</label>
          <input
            id="rule-alt-max"
            type="number"
            min={0}
            max={altitudeMax(units)}
            step={altitudeStep(units)}
            value={feetToInput(draft.alt_max_ft ?? 5000, units)}
            onChange={(e) => set("alt_max_ft", inputToFeet(Number(e.target.value), units))}
          />
        </div>
      </div>
      <p className="hint">{units === "metric" ? t.alerts.altHintMetric : t.alerts.altHint}</p>

      <label htmlFor="rule-size">{t.alerts.size}</label>
      <select
        id="rule-size"
        value={draft.min_size ?? "any"}
        onChange={(e) => set("min_size", e.target.value as MinSize)}
      >
        <option value="any">{t.alerts.sizeAny}</option>
        <option value="large">{t.alerts.sizeLarge}</option>
        <option value="heavy">{t.alerts.sizeHeavy}</option>
        <option value="four_engine">{t.alerts.sizeFour}</option>
      </select>
      {/* Hidden when nothing is being tracked — at night, with every rule asleep, the poller is idle
          and "0 of 0" would say nothing about the filter. */}
      {preview && preview.tracked > 0 && (
        <p className="hint preview">
          {t.alerts.preview(preview.matching, preview.tracked)}
        </p>
      )}

      <label htmlFor="rule-lead">
        {t.alerts.leadTime} — <strong>{mins(draft.lead_time_seconds ?? 120)}</strong>
      </label>
      <input
        id="rule-lead"
        type="range"
        min={0}
        max={480}
        step={30}
        value={draft.lead_time_seconds ?? 120}
        onChange={(e) => set("lead_time_seconds", Number(e.target.value))}
      />
      <p className="hint">{t.alerts.leadHint}</p>

      <label htmlFor="rule-cooldown">
        {t.alerts.cooldown} — <strong>{draft.cooldown_minutes ?? 60} min</strong>
      </label>
      <input
        id="rule-cooldown"
        type="range"
        min={0}
        max={360}
        step={15}
        value={draft.cooldown_minutes ?? 60}
        onChange={(e) => set("cooldown_minutes", Number(e.target.value))}
      />

      <label className="checkbox">
        <input
          type="checkbox"
          checked={draft.daylight_only ?? true}
          onChange={(e) => set("daylight_only", e.target.checked)}
        />
        {t.alerts.daylightOnly}
      </label>
      <p className="hint">{t.alerts.daylightHint}</p>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={draft.respect_quiet_hours ?? true}
          onChange={(e) => set("respect_quiet_hours", e.target.checked)}
        />
        {t.alerts.respectQuiet}
      </label>

      {/* The draft as it stands, saved or not — through the real wording and push path, clearly
          labelled as a test, leaving no trace in history. */}
      <button className="secondary" onClick={() => void sendTest()} disabled={testing || !draft.name.trim()}>
        {testing ? t.common.working : t.alerts.sendTest}
      </button>
      {testResult && <p className="hint">{testResult}</p>}

      {error && <p className="error">{error}</p>}

      <div className="row">
        <button onClick={save} disabled={busy || !draft.name.trim()}>
          {busy ? t.common.saving : t.common.save}
        </button>
        <button className="secondary" onClick={onCancel} disabled={busy}>
          {t.common.cancel}
        </button>
      </div>
      {onDelete && (
        <button className="danger" onClick={() => void onDelete()} disabled={busy}>
          {t.alerts.delete}
        </button>
      )}
    </div>
  );
}

function RecentAlerts() {
  const [items, setItems] = useState<RecentAlert[] | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    sightingsApi.recentAlerts(20).then(setItems).catch(() => setItems([]));
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <section>
      <h2>{t.alerts.recentTitle}</h2>
      {items.map((a) => (
        <button key={a.id} className="card-row recent" onClick={() => navigate(`/?focus=${a.aircraft_hex}`)}>
          <div className="card-main">
            <div className="card-title">
              {a.description ?? a.icao_type ?? a.aircraft_hex.toUpperCase()}
              {a.callsign && <span className="dim"> · {a.callsign}</span>}
            </div>
            <div className="card-sub">
              {[
                a.notified_at ? ago(a.notified_at) : null,
                a.rule_name,
                a.closest_distance_m !== null ? `${t.alerts.closest} ${km(Math.round(a.closest_distance_m / 100) * 100)}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
          <span className="chevron">›</span>
        </button>
      ))}
    </section>
  );
}

export default function Alerts() {
  const [list, setList] = useState<AlertRule[] | null>(null);
  const [editing, setEditing] = useState<AlertRule | "new" | null>(null);
  const { settings } = useSettings();

  const reload = useCallback(async () => {
    setList(await rulesApi.list().catch(() => []));
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const units: Units = settings?.units ?? "aviation";
  const anchorLat = settings?.home_lat ?? settings?.live_lat ?? null;
  const anchorLon = settings?.home_lon ?? settings?.live_lon ?? null;

  async function save(draft: Partial<RuleDraft> & { name: string }) {
    if (editing === "new") await rulesApi.create(draft);
    else if (editing) await rulesApi.update(editing.id, draft);
    setEditing(null);
    await reload();
  }

  async function toggle(rule: AlertRule) {
    await rulesApi.update(rule.id, { enabled: !rule.enabled });
    await reload();
  }

  return (
    <div className="page">
      <h1>{t.alerts.title}</h1>

      {editing ? (
        <Editor
          rule={editing === "new" ? null : editing}
          units={units}
          anchorLat={anchorLat}
          anchorLon={anchorLon}
          onSave={save}
          onDelete={
            editing === "new"
              ? null
              : async () => {
                  await rulesApi.remove(editing.id);
                  setEditing(null);
                  await reload();
                }
          }
          onCancel={() => setEditing(null)}
        />
      ) : (
        <>
          {list !== null && settings && <SetupChecklist rules={list} settings={settings} />}
          {list === null && <p className="dim">{t.loading}</p>}
          {list?.length === 0 && <p className="dim">{t.alerts.empty}</p>}

          {list?.map((rule) => (
            <div key={rule.id} className="card-row">
              <button className="card-main" onClick={() => setEditing(rule)}>
                <div className="card-title">{rule.name}</div>
                <div className="card-sub">{summary(rule, units)}</div>
                {/* The most likely answer to "I set an alert and nothing happened" (PLAN D4),
                    so it is shown on the rule itself rather than buried in a help page. */}
                {rule.dormant_reason && <div className="warn">{t.alerts.dormant}: {rule.dormant_reason}</div>}
                {!rule.dormant_reason && rule.sleep_reason && (
                  <div className="card-sub asleep">
                    {rule.sleep_reason === "night"
                      ? rule.wakes_at
                        ? t.alerts.asleepUntil(clock(rule.wakes_at))
                        : t.alerts.asleepNoSunrise
                      : rule.wakes_at
                        ? t.alerts.asleepQuietUntil(clock(rule.wakes_at))
                        : t.alerts.asleepQuiet}
                  </div>
                )}
                <div className="card-sub">
                  {rule.last_notified_at
                    ? t.alerts.lastAlert(ago(rule.last_notified_at), rule.notified_last_7d)
                    : t.alerts.neverAlerted}
                </div>
              </button>
              <label className="switch" aria-label={rule.enabled ? t.alerts.disable : t.alerts.enable}>
                <input type="checkbox" checked={rule.enabled} onChange={() => void toggle(rule)} />
                <span />
              </label>
            </div>
          ))}

          <button onClick={() => setEditing("new")}>{t.alerts.create}</button>

          {list && list.some((r) => r.daylight_only) && <p className="hint">{t.alerts.daylightDefaultNote}</p>}

          <Link to="/types" className="card-row link-row">
            <div className="card-main">
              <div className="card-title">{t.alerts.typesLink}</div>
              <div className="card-sub">{t.alerts.typesHint}</div>
            </div>
            <span className="chevron">›</span>
          </Link>

          <RecentAlerts />
        </>
      )}
    </div>
  );
}
